<template>
  <div class="user-page">
    <PageHeader :title="$t('certification.my_certification')" icon="uil-file-check-alt">
      <template #right>
        <button class="cert-add-btn" @click="showAddCertificateModal = true">
          <i class="uil-plus"></i>
          {{ $t('certificate.add') }}
        </button>
      </template>
    </PageHeader>


    <SfInput v-model="search"
             :placeholder="$t('certification.search_certification')"
             icon="uil-search"
             name="cert-search"
             @input="isTyping = true"
    />
    <div uk-grid>
      <div class="uk-width-5-5@m">
        <!-- Skeleton Loading -->
        <template v-if="isLoading">
          <CertificateCardSkeleton v-for="n in 3" :key="'skeleton-' + n"/>
        </template>

        <!-- Certificate Cards -->
        <template v-else>
          <CertificateCard v-for="(certification, index) in certifications"
                           :key="certification.id || index"
                           :certification="certification"
                           :is-downloading="downloadingId === certification.id"
                           :is-sharing="sharingStates[certification.id]"
                           @download="handleToDownloadPdf"
                           @preview="showCertificateModal"
                           @share="shareToLinkedIn"
          />
        </template>
      </div>
    </div>

    <!-- Certificate Preview Modal -->
    <AppModal :bottom-sheet="true"
              :title="$t('certification.preview')"
              :visible.sync="showPreviewModal"
              max-height="90vh"
              width="90%"
              class="preview-modal"
              @close="clearPreviewModal">
      <CertificatePreview :preview-link="pdf_preview_link"/>

      <template #footer>
        <button :disabled="isModalDownloading"
                class="btn btn-success"
                type="button"
                @click="handleToDownloadPdf(currentCertificate)"
        >
          <i :class="isModalDownloading ? 'uil-spinner-alt spin-icon' : 'uil-file-download'"></i>
          {{ isModalDownloading ? ($t('general.downloading') || 'İndiriliyor...') : $t('general.download') }}
        </button>
      </template>
    </AppModal>

    <pagination :pagination="pagination" class="mt-2 mb-3" @changePage="setPage($event)"></pagination>

    <AppModal :bottom-sheet="true"
              :title="$t('certificate.add')"
              :visible.sync="showAddCertificateModal"
              max-height="90vh"
              width="800px"
              @close="showAddCertificateModal = false">
      <AddCertificateModal v-if="showAddCertificateModal"
                           ref="addCertModal"
                           :loading="modalLoading"
                           @submit="handleCertificateSubmit"
      />
      <template #footer>
        <button class="btn btn-outline-secondary" type="button" @click="showAddCertificateModal = false">
          {{ $t('general.cancel') }}
        </button>
        <button :disabled="modalLoading" class="cert-add-btn" type="button"
                @click="$refs.addCertModal && $refs.addCertModal.onSubmit()">
          {{ $t('general.save') }}
        </button>
      </template>
    </AppModal>
  </div>
</template>
<script>

import { GET_ITEMS } from "@/core/services/store/REST.module";
import ApiService from "@/core/services/api.service";
import moment from "moment";
import 'moment/locale/fr';
import 'moment/locale/es';
import 'moment/locale/az';
import 'moment/locale/tr';
import 'moment/locale/en-gb';
import Pagination from "@/assets/components/Pagination.vue";
import axios from "axios";
import AddCertificateModal from "@/view/components/modal/AddCertificateModal.vue";
import CertificateCard from "@/view/components/card/CertificateCard.vue";
import CertificateCardSkeleton from "@/view/components/card/CertificateCardSkeleton.vue";
import PageHeader from "@/view/components/common/PageHeader.vue";
import CertificatePreview from "./components/CertificatePreview.vue";
import AppModal from "@/view/components/ui/AppModal.vue";
import SfInput from "@/view/components/ui/SfInput.vue";
import { showError, showSuccess } from "@/utils/alertUtils.js";
import { sanitizeFileName } from "@/utils/stringUtils.js";
import bridge from "@/core/services/bridge.service";
import native from "@/mobile/mobile.fileHandler";

export default {
  name: "MyCertification",
  components: {
    Pagination,
    AddCertificateModal,
    CertificateCard,
    CertificateCardSkeleton,
    PageHeader,
    CertificatePreview,
    AppModal,
    SfInput
  },
  data() {
    return {
      pagination: null,
      certificationsUrl: 'api/get-certificates',
      certifications: [],
      search: '',
      order_by: 'created_at',
      sort: 'desc',
      isTyping: false,
      searchResult: [],
      page: 1,
      perPage: 10,
      pdf_preview_link: '',
      certificate_download_link: null,
      sharingStates: {}, // Her certification ID'si için loading durumu
      downloadingId: null, // Aktif indirme olan certification ID
      showPreviewModal: false,
      showAddCertificateModal: false,
      modalLoading: false,
      currentCertificate: null,
      isLoading: true,
    };
  },
  methods: {
    setPage(page) {
      this.page = page;
      this.applyFilter()
    },
    handleToDownloadPdf(item) {
      if (!item) {
        showError("Sertifika bilgisi bulunamadı")
        return
      }

      const downloadUrl = item.certificate_pdf_download_link;

      if (!downloadUrl) {
        showError("Sertifika indirilirken hata oluştu")
        return
      }

      this.certificate_download_link = downloadUrl;

      const fileName = item.certificate?.name
          ? `${sanitizeFileName(item.certificate.name)}.pdf`
          : 'sertifika.pdf';

      if (bridge.isNative) {
        this.downloadingId = item.id;
        native.downloadFile(downloadUrl, fileName, 'application/pdf');
      } else {
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.target = '_blank';
        a.rel = 'noopener';
        a.click();
      }
    },

    showCertificateModal(item) {
      this.currentCertificate = item;

      const previewUrl = item.certificate_pdf_preview_link;

      if (previewUrl) {
        this.pdf_preview_link = previewUrl;
        this.certificate_download_link = item.certificate_pdf_download_link || previewUrl;
        this.showPreviewModal = true;
      } else {
        showError("Sertifika görüntülenirken hata oluştu")
      }
    },
    clearPreviewModal() {
      this.showPreviewModal = false;
      this.pdf_preview_link = '';
      this.certificate_download_link = null;
      this.currentCertificate = null;
    },
    async shareToLinkedIn(certification) {
      try {
        this.$set(this.sharingStates, certification.id, true);

        const response = await axios.get(`api/certification/share/${certification.certificate_encrypted_link}`);
        const data = response.data;

        const shareUrl = encodeURIComponent(data.certificate_img_url);
        const title = encodeURIComponent(data.title);
        const linkedinUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}&summary=Sertifika&source=LinkedIn&text=${title}`;

        window.open(linkedinUrl, '_blank');
      } catch (error) {
        UIkit.notification({
          message: this.$t('general.share_preparation_error'),
          status: 'danger',
          pos: 'top-right',
          timeout: 3000
        });
      } finally {
        this.$set(this.sharingStates, certification.id, false);
      }
    },
    fixDate(value) {
      if (!value) return '---'

      return moment(value).locale(this.timeZone()).format('DD MMMM YYYY')
    },
    applyFilter: function (search) {
      let self = this;
      if (this.search) {
        self.certifications = [];
        self.page = 1;
      }
      this.$store.dispatch(GET_ITEMS, {
        url: this.certificationsUrl,
        filters: {
          'page': this.page,
          'perPage': this.perPage,
          'search': this.search,
          'order_by': this.order_by,
        },
        acceptPromise: false,
      }).then(result => {
        self.certifications = result.data.data;
        this.pagination = result.data;
        this.isLoading = false;
      })
    },

    async handleCertificateSubmit(formData) {
      this.modalLoading = true;
      try {
        await ApiService.post('/api/user-certificate', formData);
        this.showAddCertificateModal = false;
        this.applyFilter();
        showSuccess('Sertifika başarıyla eklendi!');
      } catch (e) {
        const errors = e?.response?.data?.errors;
        if (errors && this.$refs.addCertModal) {
          const firstError = Object.values(errors).flat()[0];
          this.$refs.addCertModal.alertMessage = { type: 'error', text: firstError };
        } else {
          showError(e?.response?.data?.message || 'Sertifika eklenirken hata oluştu!');
        }
      } finally {
        this.modalLoading = false;
      }
    },
  },
  mounted() {
    this.applyFilter();

    // Native'den indirme bitti mesajını dinle
    if (bridge.isNative) {
      this._unsubDownloadDone = bridge.on('DOWNLOAD_DONE', (res) => {
        this.downloadingId = null;
      });
    }
  },
  beforeDestroy() {
    this._unsubDownloadDone?.();
  },
  computed: {
    isModalDownloading() {
      return this.downloadingId != null && this.currentCertificate && this.downloadingId === this.currentCertificate.id;
    },
  },
  watch: {
    search: _.debounce(function () {
      this.isTyping = false;
    }, 1000),
    isTyping: function (value) {
      if (!value) {
        this.applyFilter(this.search);
      }
    }
  }
}
</script>

<style scoped>
.user-page {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.cert-subtitle {
  font-size: 16px;
  color: #666;
  line-height: 1.5;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.spin-icon {
  display: inline-block;
  animation: spin 1s linear infinite;
}

.cert-add-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 44px;
  padding: 0 20px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  background: #2c3e50;
  border: 2px solid #2c3e50;
  border-radius: 10px;
  cursor: pointer;
  outline: none;
  box-shadow: none;
  -webkit-tap-highlight-color: transparent;
}

.cert-add-btn:hover,
.cert-add-btn:focus,
.cert-add-btn:active {
  background: #1a2a3a;
  border-color: #1a2a3a;
  color: #fff;
  outline: none;
  box-shadow: none;
  transform: scale(0.98);
  opacity: 0.85;
}

@media (max-width: 768px) {
  .user-page :deep(.page-header-block),
  .user-page :deep(.page-header-block__left),
  .user-page :deep(.page-header-block__title) {
    min-width: 0;
  }

  .user-page :deep(.page-header-block) {
    gap: 8px;
  }

  .user-page :deep(.page-header-block__title) {
    font-size: 18px;
  }

  .cert-add-btn {
    min-height: 48px;
    padding: 0 16px;
    font-size: 16px;
  }

  .cert-subtitle {
    font-size: 14px;
  }
}

@media (max-width: 575px) {

  .user-page :deep(.page-header-block) {
    flex-wrap: nowrap;
  }

  .user-page :deep(.page-header-block__right) {
    width: auto;
    flex-shrink: 0;
    justify-content: flex-end;
  }

  .user-page :deep(.page-header-block__title) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .user-page .cert-add-btn {
    min-width: 44px;
    padding-left: 10px;
    padding-right: 10px;
    font-size: 14px;
  }

  .user-page :deep(.sf-input-wrapper) {
    margin-bottom: 16px;
  }

  .user-page :deep(.app-modal) {
    max-width: 100% !important;
  }

  .user-page :deep(.app-modal-body) {
    padding: 16px;
  }

  .user-page :deep(.preview-modal .app-modal-body) {
    padding: 0;
  }

  .user-page :deep(.preview-container) {
    min-height: 240px;
  }

  .user-page :deep(.preview-iframe) {
    min-height: 62vh;
    max-height: 70vh;
  }

  .user-page :deep(.app-modal-footer) {
    padding: 12px 16px max(12px, env(safe-area-inset-bottom));
  }

  .user-page :deep(.app-modal-footer .btn),
  .user-page :deep(.app-modal-footer .cert-add-btn) {
    min-height: 48px;
    width: 100%;
    box-sizing: border-box;
  }
}
</style>

<style>
/* Preview modal — iframe modal body'yi tam kaplasın */
.preview-modal .app-modal-body {
  padding: 0 !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
}

/* Modal footer butonları — mobilde 48px touch target */
@media (max-width: 768px) {
  .app-modal-footer .btn {
    min-height: 48px;
    font-size: 16px;
    border-radius: 10px;
    -webkit-tap-highlight-color: transparent;
  }
}
</style>
